import { css } from '@emotion/react';
import { AlertComponent } from '~/_shared/baseComponents/alert';
import { OverlayWrapperComponent } from '~/_shared/components/overlay/overlayWrapper.component';
import { useTranslation } from '~/_shared/utils/hooks';
import { ExportReportType } from '~/exportReport/exportReportType';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import { ExportReportTemplateRendererComponent } from '~/exportReport/templateRenderer/exportReportTemplateRenderer.component';
import { useExportReportParams } from '~/exportReport/useExportReportParams';
import { useLatLngSpreadsheetData } from '~/map/map/useSpreadsheetData.hook';
import type { DemographicsResult } from '~/store/demographics/demographics.repository';
import type { ExportReportLocationParams } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.state';

const contentStyle = css({
  padding: 20,
  maxWidth: 1000,
  margin: '0 auto',
});

const googleMapStyle = css({
  width: '50%',
  aspectRatio: '386 / 234',
});

type MapExportReportPreviewLocationProps = Readonly<{
  params: ExportReportLocationParams;
  template: ReportTemplate;
  isError: boolean;
  isPreviewMode: boolean;
  getDemographicLabel: (demographicId: string) => string | null;
  getDemographicData: (columnId: string, demographicId: string) => DemographicsResult | null;
}>;

export const MapExportReportPreviewLocationComponent = ({
  template, params, isError, isPreviewMode, getDemographicData, getDemographicLabel,
}: MapExportReportPreviewLocationProps) => {
  const [t] = useTranslation();

  const spreadsheetData = useLatLngSpreadsheetData();
  const rowData = spreadsheetData.getRow(params.data.markerId);
  const { tableColumns } = useExportReportParams();

  if (!rowData) {
    return null;
  }

  return (
    <div>
      {isError && (
        <AlertComponent type="danger">{t('Error Exporting a Report')}</AlertComponent>
      )}

      <div css={contentStyle}>
        <OverlayWrapperComponent>
          <ExportReportTemplateRendererComponent
            template={template}
            tableColumns={tableColumns}
            summaryProps={{
              latLng: {
                lat: rowData.lat,
                lng: rowData.lng,
              },
              heading: params.data.summary.heading,
              semiHeading: params.data.summary.semiHeading,
              description: params.data.summary.description,
            }}
            mapProps={{
              center: rowData,
              css: googleMapStyle,
              type: ExportReportType.Location,
            }}
            getDemographicLabel={getDemographicLabel}
            getDemographicData={getDemographicData}
            isPreviewMode={isPreviewMode}
          />
        </OverlayWrapperComponent>
      </div>
    </div>
  );
};
